import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { EmptyState } from "@/components/common/EmptyState";
import { RefreshCw, Inbox, AlertTriangle, CheckCircle2, Link2, Archive, Ban } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");
const STATUS = { connected: ["Connected", "bg-emerald-100 text-emerald-700"], pending: ["Waiting for first sync", "bg-slate-100 text-slate-600"],
  error: ["Error — will retry", "bg-amber-100 text-amber-800"], needs_reauth: ["Needs Reauthorization", "bg-rose-100 text-rose-700"], disabled: ["Off", "bg-slate-100 text-slate-500"] };

export function useEmailSync() {
  const [st, setSt] = useState(null);
  const load = useCallback(() => api.get("/email-sync/status").then((r) => setSt(r.data)).catch(() => setSt(false)), []);
  useEffect(() => { load(); }, [load]);
  return [st, load];
}

function SyncNow({ userId, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/email-sync/run", { user_id: userId });
      toast.success(data.running ? "A check is already running" : "Checking for new email in the background…");
      setTimeout(onDone, 4000);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid={`sync-now-${userId}`}><RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />Sync Email Now</Button>;
}

export function MailboxStatusRow({ m, onChanged, showOwner }) {
  const [label, cls] = STATUS[m.status] || STATUS.pending;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3" data-testid={`mailbox-status-${m.user_id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 truncate">{showOwner ? `${m.owner_name} · ` : ""}{m.mailbox}</p>
        <p className="text-xs text-slate-500">Last successful sync: <b data-testid={`mailbox-last-success-${m.user_id}`}>{fmt(m.last_success_at)}</b> · Next scheduled: {fmt(m.next_scheduled_at)}</p>
        {m.last_error && m.status !== "connected" && <p className="text-xs text-amber-700" data-testid={`mailbox-error-${m.user_id}`}>Last error: {m.last_error}{m.next_retry_at ? ` · retry after ${fmt(m.next_retry_at)}` : ""}</p>}
      </div>
      <span className={`text-xs rounded-full px-2 py-0.5 ${cls}`} data-testid={`mailbox-state-${m.user_id}`}>{label}</span>
      {m.enabled && m.status !== "needs_reauth" && <SyncNow userId={m.user_id} onDone={onChanged} />}
    </div>
  );
}

// Owner opt-in card (Email & Calendar settings).
export function EmailSyncCard() {
  const [st, load] = useEmailSync();
  if (!st || !st.mine) return null;
  const m = st.mine;
  const toggle = async (v) => { try { await api.post("/email-sync/enable", { enabled: v }); toast.success(v ? "Email reply sync turned on" : "Email reply sync turned off"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm mb-5" data-testid="email-sync-card">
      <div className="flex items-center gap-3">
        <Inbox className="w-5 h-5 text-[#006090]" />
        <h3 className="font-semibold text-slate-800">Log email replies automatically</h3>
        <Switch className="ml-auto" checked={m.enabled} onCheckedChange={toggle} data-testid="email-sync-toggle" />
      </div>
      <p className="text-xs text-slate-500 mt-1.5">Top-Recruit checks this mailbox in the background — hourly during business hours on weekdays and once a day on weekends — and logs replies from your candidates and agents, plus delivery and read receipts. Unrelated email is never imported. The first check looks back 30 days.</p>
      {m.enabled && <div className="mt-2 border-t border-slate-100"><MailboxStatusRow m={m} onChanged={load} /></div>}
      {m.status === "needs_reauth" && <p className="text-xs text-rose-700 mt-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Click Reconnect above to resume syncing.</p>}
    </div>
  );
}

function HoursEditor({ settings, tz, onSaved }) {
  const [f, setF] = useState(settings);
  const save = async () => { try { await api.put("/email-sync/settings", f); toast.success("Sync hours saved"); onSaved(); } catch (e) { toast.error(apiError(e)); } };
  const num = (k, label) => <label className="text-xs text-slate-600 flex items-center gap-1.5">{label}
    <Input type="number" min={0} max={24} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-8 w-16" data-testid={`sync-hours-${k}`} /></label>;
  return <div className="flex flex-wrap items-end gap-3" data-testid="sync-hours">
    {num("business_start", "Weekdays from")}{num("business_end", "to")}{num("weekend_hour", "Weekend check at")}
    <span className="text-xs text-slate-400">Hours (0–23) in {tz}</span>
    <Button size="sm" variant="outline" onClick={save} data-testid="sync-hours-save">Save</Button>
  </div>;
}

function LinkPicker({ item, onDone }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get("/persons", { params: { q, page_size: 6, all_districts: true } }).then((r) => setRes(r.data.persons || [])).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q]);
  const link = async (pid) => { try { await api.post(`/email-sync/unmatched/${item.id}/link`, { person_id: pid }); toast.success("Linked to Person"); onDone(); } catch (e) { toast.error(apiError(e)); } };
  return <div className="mt-2 space-y-1.5">
    {item.candidates?.map((c) => <Button key={c.person_id} size="sm" variant="outline" className="mr-1.5 h-7" onClick={() => link(c.person_id)} data-testid={`unmatched-link-cand-${item.id}-${c.person_id}`}><Link2 className="w-3 h-3 mr-1" />Link to {c.name}</Button>)}
    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people to link…" className="h-8 max-w-xs" data-testid={`unmatched-search-${item.id}`} />
    {res.map((p) => <button key={p.id} onClick={() => link(p.id)} data-testid={`unmatched-link-${item.id}-${p.id}`} className="block text-left text-sm text-brand hover:underline">{p.first_name} {p.last_name} <span className="text-slate-400">{p.email}</span></button>)}
  </div>;
}

function UnmatchedRow({ u, onDone }) {
  const [open, setOpen] = useState(false);
  const dismiss = async (as) => { try { await api.post(`/email-sync/unmatched/${u.id}/dismiss`, { as }); toast.success(as === "non_crm" ? "Marked as non-CRM" : "Ignored"); onDone(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="px-4 py-3" data-testid={`unmatched-${u.id}`}>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800 truncate">{u.subject || "(no subject)"}</p>
          <p className="text-xs text-slate-500">From {u.from_name ? `${u.from_name} · ` : ""}{u.from_email} · {fmt(u.received_at)} · Mailbox {u.mailbox_email}</p>
          {u.body_preview && <p className="text-xs text-slate-400 line-clamp-2 mt-0.5">{u.body_preview}</p>}
          {u.candidates?.length > 0 && <p className="text-xs text-amber-700 mt-0.5">Possible match: {u.candidates.map((c) => c.name).join(", ")}</p>}
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Button size="sm" className="h-7 bg-brand hover:bg-brand-strong text-white" onClick={() => setOpen(!open)} data-testid={`unmatched-open-link-${u.id}`}><Link2 className="w-3 h-3 mr-1" />Link</Button>
          <Button size="sm" variant="outline" className="h-7" onClick={() => dismiss("ignored")} data-testid={`unmatched-ignore-${u.id}`}><Archive className="w-3 h-3 mr-1" />Ignore</Button>
          <Button size="sm" variant="outline" className="h-7" onClick={() => dismiss("non_crm")} data-testid={`unmatched-noncrm-${u.id}`}><Ban className="w-3 h-3 mr-1" />Not CRM</Button>
        </div>
      </div>
      {open && <LinkPicker item={u} onDone={onDone} />}
    </div>
  );
}

// Communications → Email Sync tab: status (own or group mailboxes), sync hours (admins), unmatched review queue.
export default function EmailSyncPanel() {
  const [st, load] = useEmailSync();
  const [items, setItems] = useState(null);
  const loadQ = useCallback(() => api.get("/email-sync/unmatched").then((r) => setItems(r.data.items)).catch(() => setItems([])), []);
  useEffect(() => { loadQ(); }, [loadQ]);
  const refresh = () => { load(); loadQ(); };
  if (st === null) return null;
  const rows = st?.mailboxes || [];
  return (
    <div className="space-y-5" data-testid="email-sync-panel">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand" />Microsoft Email Sync</h3>
        {rows.length ? <div className="divide-y divide-slate-100">{rows.map((m) => <MailboxStatusRow key={m.user_id} m={m} onChanged={refresh} showOwner={st.can_admin} />)}</div>
          : <p className="text-sm text-slate-500 mt-2" data-testid="email-sync-none">No connected mailboxes yet. Connect Microsoft 365 and turn on "Log email replies automatically" in My Profile → Connected Accounts.</p>}
        {st?.can_edit_settings && <div className="mt-3 pt-3 border-t border-slate-100"><HoursEditor settings={st.settings} tz={st.time_zone} onSaved={load} /></div>}
      </div>
      <div>
        <h3 className="font-semibold text-slate-800 mb-2">Unmatched email <span className="text-slate-400 font-normal text-sm">— replies we couldn't confidently attach to a Person</span></h3>
        {!items ? null : items.length ? <div className="rounded-xl border border-slate-200 bg-white shadow-sm divide-y" data-testid="unmatched-list">{items.map((u) => <UnmatchedRow key={u.id} u={u} onDone={refresh} />)}</div>
          : <EmptyState icon={Inbox} title="Nothing to review" description="Unmatched replies appear here for review." />}
      </div>
    </div>
  );
}
